import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useParams, useHistory } from 'react-router-dom';
import moment from 'moment';

import CreateSwapModal from '../Components/CreateSwapModal';
import LatestTransactionsPanel from '../Components/LatestTransactionsPanel';
import CreateYourOwnSwapPanel from '../Components/CreateYourOwnSwapPanel';
import ProjectFieldColab from '../Components/ProjectFieldColab';
import TelegramButtonComponent from '../Components/TelegramButtonComponent';
import LoginModal from '../Components/LoginModal';

import {
  convertLoopToIcx,
  round,
  formatNumber,
  convertHexToDecimal,
} from '../utils/Conversions';

import { useIconSwapService } from '../Components/IconSwapService.jsx';

import tick from '../images/Tick.png';
import goBack from '../images/GoBack.svg';
import TAP from '../images/TAP.svg';
import AC3 from '../images/ac3.svg';
import SPORTS from '../images/sports.svg';
import WOK from '../images/weblok.svg';
import ICX from '../images/ICX.png';
import TransactionHistoryGraphPanel from '../Components/TransactionHistoryPanel';

const swapTypes = {
  AC3: 'cx9ab3078e72c8d9017194d17b34b1a47b661945ca',
  SPORTS: 'cx08eb683a53d6f244d538a45f0cc584825e2cb985',
  TAP: 'cxc0b5b52c9f8b4251a47e91dda3bd61e5512cd782',
  WOK: 'cx921205acb7c51e16d5b7cbc37539a4357d929d20',
  ICX: 'cx0000000000000000000000000000000000000000',
};

export const MakeSwap = ({ showLogin }: MakeSwapsProps) => {
  let history = useHistory();
  const { cancelSwap, address, fillOrder, getSwap } = useIconSwapService();
  const type: swapTypeType = useParams();
  const swapType = type.swapType;
  const swapId = type.swapId;
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isSwapModalOpen, setIsSwapModalOpen] = useState(false);
  const [isTradeComplete, setIsTradeComplete] = useState(false);
  const [thisSwap, setThisSwap] = useState();
  const [isCancelled, setIsCancelled] = useState<boolean>();

  useEffect(() => {
    const getThisSwap = async () => {
      // @ts-ignore
      const swap = await getSwap(swapId);
      setThisSwap(swap);
    };
    closeAuthModal();
    getThisSwap();
    getWindowDimensions();
  }, [swapId]);

  const getWindowDimensions = () => {
    const { innerWidth: width, innerHeight: height } = window;
    return {
      width,
      height,
    };
  };
  const { innerWidth: width } = window;

  const openAuthModal = () => {
    setIsAuthModalOpen(true);
  };

  const closeAuthModal = () => {
    setIsAuthModalOpen(false);
  };

  const openSwapModal = () => {
    setIsSwapModalOpen(true);
  };

  const closeSwapModal = () => {
    setIsSwapModalOpen(false);
  };

  const showIcon = (type: string) => {
    switch (type) {
      case 'cx9ab3078e72c8d9017194d17b34b1a47b661945ca':
        return AC3;
      case 'cx08eb683a53d6f244d538a45f0cc584825e2cb985':
        return SPORTS;
      case 'cx921205acb7c51e16d5b7cbc37539a4357d929d20':
        return WOK;
      case 'cxc0b5b52c9f8b4251a47e91dda3bd61e5512cd782':
        return TAP;
      case 'cx0000000000000000000000000000000000000000':
        return ICX;
      default:
        return;
    }
  };

  const showSymbol = (type: string) => {
    switch (type) {
      case 'cx9ab3078e72c8d9017194d17b34b1a47b661945ca':
        return 'AC3';
      case 'cx08eb683a53d6f244d538a45f0cc584825e2cb985':
        return 'SPORTS';
      case 'cx921205acb7c51e16d5b7cbc37539a4357d929d20':
        return 'WOK';
      case 'cxc0b5b52c9f8b4251a47e91dda3bd61e5512cd782':
        return 'TAP';
      case 'cx0000000000000000000000000000000000000000':
        return 'ICX';
      default:
        return;
    }
  };

  const returnSymbol = (code: string) => {
    return Object.keys(swapTypes).filter(
      // @ts-ignore
      (key) => swapTypes[key] === code
    )[0];
  };

  const latestTransactionToken = () => {
    if (
      thisSwap.maker.contract === 'cx0000000000000000000000000000000000000000'
    ) {
      return thisSwap.taker.contract;
    }

    return thisSwap.maker.contract;
  };

  const calculateSwapPrice = (maker: string, taker: string) => {
    const convertedMaker = convertLoopToIcx(maker);
    const convertedTaker = convertLoopToIcx(taker);
    const price = convertedTaker / convertedMaker;
    const roundedPrice = round(price, 5);
    return roundedPrice;
  };

  const handleViewTransactionClick = () => {
    window.open(`https://iconwat.ch/tx/0x${thisSwap.transaction}`, '_blank');
  };

  const handleCancelSwap = async () => {
    // @ts-ignore
    const cancelled = await cancelSwap(swapId);
    if (cancelled) {
      setIsCancelled(true);
    }
  };

  const showCorrectHeaderComponent = () => {
    if (thisSwap.status === 'SUCCESS' || isCancelled) return;
    if (!isTradeComplete && thisSwap.maker.provider === address) {
      return (
        <WithdrawButton
          onClick={() => {
            handleCancelSwap();
          }}
        >
          <WithdrawButtonText>Withdraw your swap</WithdrawButtonText>
        </WithdrawButton>
      );
    }
    if (isTradeComplete) {
      return (
        <SwappedIcon>
          <TickIcon src={tick} alt="Tick" />
          <SwappedText>Swapped</SwappedText>
        </SwappedIcon>
      );
    }
  };

  const completeOrderFunction = async () => {
    // @ts-ignore
    const order = await fillOrder(
      thisSwap.id,
      thisSwap.taker.contract,
      thisSwap.taker.amount
    );
    if (!order) return;
    setIsTradeComplete(true);
  };

  const showCorrectBuySwapComponent = () => {
    if (isCancelled || thisSwap.status === 'CANCELLED') {
      return (
        <BuySwapPanelGrey>
          <BuySwapHeadingText>Swap cancelled</BuySwapHeadingText>
          <BuySwapSubText>
            This swap has been cancelled and is no longer available.
          </BuySwapSubText>
        </BuySwapPanelGrey>
      );
    }

    if (thisSwap.status !== 'SUCCESS') {
      return (
        <BuySwapPanelGreen>
          <BuySwapHeadingText>Buy this swap</BuySwapHeadingText>
          <BuySwapSubText>
            You can buy this swap by depositing{' '}
            {formatNumber(convertLoopToIcx(thisSwap.taker.amount))}{' '}
            {returnSymbol(thisSwap.taker.contract)}. You will instantly receive{' '}
            {formatNumber(convertLoopToIcx(thisSwap.maker.amount))}{' '}
            {returnSymbol(thisSwap.maker.contract)} in return.
          </BuySwapSubText>
          <SwapButton
            onClick={() => {
              if (!address) {
                return width < 414
                  ? showLogin()
                  : setIsAuthModalOpen(!isAuthModalOpen);
              }
              // @ts-ignore
              completeOrderFunction();
            }}
          >
            <ButtonText>
              Deposit {formatNumber(convertLoopToIcx(thisSwap.taker.amount))}{' '}
              {showSymbol(thisSwap.taker.contract)}
            </ButtonText>
          </SwapButton>
        </BuySwapPanelGreen>
      );
    }

    if (isTradeComplete || thisSwap.status === 'SUCCESS') {
      const rawDate = convertHexToDecimal(thisSwap.timestamp_create);
      const date = moment(rawDate / 1000).format('MMMM Do, YYYY');
      return (
        <BuySwapPanelGrey>
          <BuySwapHeadingText>Swap completed</BuySwapHeadingText>
          <BuySwapSubText>
            This swap has been successfully completed on {date}. Click the
            button to view the transaction details.
          </BuySwapSubText>
          <SwapButton onClick={handleViewTransactionClick}>
            <ButtonText>View transaction</ButtonText>
          </SwapButton>
        </BuySwapPanelGrey>
      );
    }

    return;
  };

  if (!thisSwap) {
    return <div></div>;
  }

  return (
    <>
      <MakeSwapWrapper width={width}>
        <TopWrapper>
          {!isTradeComplete ? (
            <GoBackWrapper
              onClick={() => {
                history.goBack();
              }}
            >
              <GoBackArrowImage src={goBack} alt="arrow" />
              <GoBackText>Go back</GoBackText>
            </GoBackWrapper>
          ) : (
            <NoArrowDiv />
          )}
          <HeadingWrapper>
            <HeadingText>Swap #{swapId}</HeadingText>
            {showCorrectHeaderComponent()}
          </HeadingWrapper>
          <SubHeadingText>
            This swap is offering{' '}
            {thisSwap.maker.contract ===
            'cx0000000000000000000000000000000000000000'
              ? 'ICX'
              : swapType}{' '}
            in exchange for{' '}
            {thisSwap.taker.contract ===
            'cx0000000000000000000000000000000000000000'
              ? 'ICX'
              : swapType}
          </SubHeadingText>
          <TopPanelsWrapper>
            <SwappingForPanel>
              <PanelHeadingWrapper>
                <ItemIcon>
                  <BottomIcon
                    src={showIcon(thisSwap.maker.contract)}
                  ></BottomIcon>
                  <TopIcon src={showIcon(thisSwap.taker.contract)}></TopIcon>
                </ItemIcon>
                <PanelHeader>
                  Swapping{' '}
                  {thisSwap.maker.contract ===
                  'cx0000000000000000000000000000000000000000'
                    ? 'ICX'
                    : swapType}{' '}
                  for{' '}
                  {thisSwap.taker.contract ===
                  'cx0000000000000000000000000000000000000000'
                    ? 'ICX'
                    : swapType}{' '}
                </PanelHeader>
              </PanelHeadingWrapper>
              <TableHeader>
                <SellingText>Selling</SellingText>
                <BuyingText>Buying</BuyingText>
                <PriceText>Price (ICX)</PriceText>
              </TableHeader>
              <TableRow>
                <SellingColumn>
                  <SellingColumnText>
                    {formatNumber(convertLoopToIcx(thisSwap.maker.amount))}{' '}
                    {returnSymbol(thisSwap.maker.contract)}
                  </SellingColumnText>
                </SellingColumn>
                <BuyingColumn>
                  <BuyingColumnText>
                    {formatNumber(convertLoopToIcx(thisSwap.taker.amount))}{' '}
                    {returnSymbol(thisSwap.taker.contract)}
                  </BuyingColumnText>
                </BuyingColumn>
                <PriceColumn>
                  <PriceColumnText>
                    {calculateSwapPrice(
                      thisSwap.maker.amount,
                      thisSwap.taker.amount
                    )}
                  </PriceColumnText>
                </PriceColumn>
              </TableRow>
            </SwappingForPanel>
            <TopPadding />
            {showCorrectBuySwapComponent()}
          </TopPanelsWrapper>
        </TopWrapper>
        <GreyLine />
        <PanelWrapper>
          <TransactionHistoryGraphPanel swapType={swapType} />
        </PanelWrapper>
        <BottomPanelsWrapper>
          <LatestTransactionsPanel token={latestTransactionToken()} />
          <BottomPadding />
          <CreateYourOwnSwapPanel
            createSwap={openSwapModal}
            swapType={swapType}
            responsiveLogin={showLogin}
            login={openAuthModal}
          />
        </BottomPanelsWrapper>
        {isTradeComplete && (
          <SuccessfullTrade>
            <SuccessfullTradeText>
              Congratulations, you have successfully completed a swap!
            </SuccessfullTradeText>
          </SuccessfullTrade>
        )}
        <ProjectFieldColab />
        <TelegramButtonComponent />
      </MakeSwapWrapper>
      <LoginModal isModalOpen={isAuthModalOpen} closeModal={closeAuthModal} />
      <CreateSwapModal
        isModalOpen={isSwapModalOpen}
        closeModal={closeSwapModal}
      />
    </>
  );
};

export default MakeSwap;

type MakeSwapsProps = {
  showLogin: () => void;
};

const MakeSwapWrapper = styled.div`
  padding-top: 55px;
  display: flex;
  align-items: center;
  flex-direction: column;
  border-bottom-left-radius: 50px;
  border-top-left-radius: 50px;
  background-color: #f1f8fc;
  ${(props: WidthType) => {
    const setWidth = props.width - 250;
    const setWidthString = setWidth.toString() + 'px';
    return css`
      width: ${setWidthString};
    `;
  }}
  padding-bottom: 50px;
  @media (max-width: 414px) {
    display: flex;
    flex-direction: column;
    padding-top: 0;
    margin-left: 0;
    width: 100vw;
    height: 2700px;
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
    padding-bottom: 0;
  }
`;

const GoBackWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  @media (max-width: 414px) {
    display: none;
  }
`;

const GoBackArrowImage = styled.img`
  height: 11.62px;
  width: 17.14px;
  margin-right: 6.77px;
  @media (max-width: 414px) {
    display: none;
  }
`;

const NoArrowDiv = styled.div`
  height: 24px;
  @media (max-width: 414px) {
    display: none;
  }
`;

const GoBackText = styled.span``;

const HeadingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 30px;
  margin-bottom: 18px;
  @media (max-width: 414px) {
    width: 100vw;
    margin-top: 0;
    height: 70px;
  }
`;

const HeadingText = styled.h1`
  color: #100f10;
  font-family: 'Tiempos Headline';
  font-size: 38px;
  font-weight: 600;
  margin-bottom: 0;
  @media (max-width: 414px) {
    color: #0e0d0e;
    font-size: 22px;
    margin-left: 20px;
  }
`;

const WithdrawButton = styled.button`
  :focus {
    outline: none;
  }
  border: none;
  height: 40px;
  width: 165px;
  border-radius: 4px;
  background-color: #f1f8fc;
  border: 1px solid #007d81;
  margin-left: 20px;
  @media (max-width: 414px) {
    margin-left: auto;
    margin-right: 20px;
  }
`;

const WithdrawButtonText = styled.span`
  font-family: 'Avenir Next Medium';
  font-size: 15px;
  color: #00888c;
`;

const SwappedIcon = styled.div`
  height: 30px;
  width: 111px;
  border-radius: 15px;
  background-color: #3d033a;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-left: 20px;
`;

const TickIcon = styled.img`
  height: 14px;
  width: 14px;
  margin-left: 15px;
`;

const SwappedText = styled.span`
  color: #ffffff;
  font-family: 'Avenir Next';
  font-size: 13px;
  font-weight: 500;
  margin-left: 8px;
  margin-top: 4px;
`;

const SubHeadingText = styled.span`
  color: #100f10;
  font-family: 'Avenir Next';
  font-size: 15px;
  font-weight: 500;
  @media (max-width: 414px) {
    color: #0e0d0e;
    width: 333px;
  }
`;

const TopPanelsWrapper = styled.div`
  margin-top: 60px;
  display: flex;
  flex-direction: row;
  @media (max-width: 414px) {
    flex-direction: column;
    margin-top: 40px;
  }
`;

const SwappingForPanel = styled.div`
  width: 508px;
  min-width: 508px;
  background-color: #fff;
  box-shadow: 2px 2px 6px 2px #e9ebec;
  border-radius: 5px;
  border-color: #e9ebec;
  border-style: solid;
  border-width: 0.5px;
  @media (max-width: 414px) {
    width: 335px;
    min-width: 335px;
    height: 192px;
  }
`;

const PanelHeadingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-left: 60px;
  padding-top: 30px;
  @media (max-width: 414px) {
    padding-top: 20px;
    padding-left: 14px;
  }
`;

const ItemIcon = styled.div`
  position: relative;
  top: 0;
  left: 0;
  @media (max-width: 414px) {
    width: 60px;
  }
`;

const TopIcon = styled.img`
  height: 51px;
  width: 51px;
  position: absolute;
  right: 28px;
  @media (max-width: 414px) {
    right: auto;
    left: 0px;
    height: 35px;
    width: 35px;
  }
`;

const BottomIcon = styled.img`
  height: 51px;
  width: 51px;
  position: relative;
  top: 0;
  left: 0;
  @media (max-width: 414px) {
    left: 20px;
    height: 35px;
    width: 35px;
  }
`;

const PanelHeader = styled.span`
  margin-left: 16px;
  color: #0e0d0e;
  font-family: 'Avenir Next';
  font-size: 26px;
  font-weight: 600;
  @media (max-width: 414px) {
    font-size: 19px;
  }
`;

const TableHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  margin-top: 20px;
  padding-bottom: 19px;
  border-bottom-style: solid;
  border-bottom-width: 1px;
  border-bottom-color: #e9ebec;
`;

const SellingText = styled.span`
  @media (max-width: 414px) {
    color: #100f10;
    font-family: 'Avenir Next';
    font-size: 15px;
  }
`;

const BuyingText = styled.span`
  @media (max-width: 414px) {
    color: #100f10;
    font-family: 'Avenir Next';
    font-size: 15px;
  }
`;

const PriceText = styled.span`
  @media (max-width: 414px) {
    color: #100f10;
    font-family: 'Avenir Next';
    font-size: 15px;
  }
`;

const TableRow = styled.div`
  border-bottom-color: #e9ebec;
  border-bottom-width: 1px;
  border-bottom-style: solid;
  height: 59px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const SellingColumn = styled.div`
  width: 148px;
  margin-left: 50px;
  @media (max-width: 414px) {
    margin: 0;
    margin-left: 10px;
  }
`;

const SellingColumnText = styled.span`
  width: 110px;
  color: #100f10;
  font-family: 'Avenir Next';
  font-size: 15px;
  font-weight: 500;
  text-align: left;
  @media (max-width: 414px) {
  }
`;

const BuyingColumn = styled.div`
  width: 148px;
  display: flex;
  @media (max-width: 414px) {
    width: 120px;
  }
`;

const BuyingColumnText = styled.span`
  margin-right: 50px;
  color: #100f10;
  font-family: 'Avenir Next';
  font-size: 15px;
  font-weight: 500;
  @media (max-width: 414px) {
    width: 120px;
  }
`;

const PriceColumn = styled.div`
  width: 128px;
  display: flex;
  @media (max-width: 414px) {
    width: 110px;
    margin-right: 0;
  }
`;

const PriceColumnText = styled.span`
  color: #100f10;
  font-family: 'Avenir Next';
  font-size: 15px;
  font-weight: 500;
  text-align: right;
  @media (max-width: 414px) {
    margin-right: 30px;
  }
`;

const BuySwapPanelGreen = styled.div`
  height: 242px;
  width: 508px;
  min-width: 508px;
  border: 1px solid #007d81;
  border-radius: 6px;
  background-color: #e1f0f4;
  box-shadow: 0 2px 5px 0 rgba(206, 210, 219, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px;
  @media (max-width: 414px) {
    width: 335px;
    min-width: 335px;
    height: 237px;
    padding-bottom: 0;
    margin-top: 40px;
  }
`;

const BuySwapPanelGrey = styled.div`
  height: 242px;
  width: 508px;
  min-width: 508px;
  border: 1px solid #3d033a;
  border-radius: 6px;
  background-color: #faf8fa;
  box-shadow: 0 2px 5px 0 rgba(206, 210, 219, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px;
  @media (max-width: 414px) {
    width: 335px;
    min-width: 305px;
    height: 260px;
    margin-top: 40px;
  }
`;

const BuySwapHeadingText = styled.span`
  color: #0e0d0e;
  font-family: 'Avenir Next';
  font-size: 26px;
  font-weight: 600;
  margin-bottom: 10px;
`;

const BuySwapSubText = styled.span`
  color: #100f10;
  font-family: 'Avenir Next';
  font-size: 15px;
`;

const SwapButton = styled.button`
  :focus {
    outline: none;
  }
  border: none;
  background-color: #007d81;
  height: 50px;
  width: 230px;
  border-radius: 4px;
  margin-top: 30px;
  @media (max-width: 414px) {
    margin-top: 20px;
  }
`;

const ButtonText = styled.span`
  font-size: 16px;
  color: #fff;
`;

const GreyLine = styled.hr`
  border-top: 1px solid #dbe2e4;
  border-bottom: 0;
  border-left: 0;
  border-right: 0;
  width: 1055px;
  margin: 0;
  margin-top: 60px;
  margin-bottom: 60px;
  @media (max-width: 414px) {
    margin-top: 40px;
    margin-bottom: 40px;
    width: 335px;
  }
`;

const BottomPanelsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  @media (max-width: 414px) {
    flex-direction: column;
  }
`;

const TopPadding = styled.div`
  width: 40px;
  min-width: 40px;
  height: 40px;
  @media (max-width: 414px) {
    display: none;
  }
`;

const BottomPadding = styled.div`
  width: 40px;
  min-width: 40px;
  height: 532px;
  @media (max-width: 414px) {
    display: none;
  }
`;

const SuccessfullTrade = styled.div`
  height: 82px;
  width: 340px;
  border-radius: 6px;
  background-color: #ffffff;
  box-shadow: 0 2px 5px 0 rgba(206, 210, 219, 0.5);
  position: absolute;
  right: 40px;
  top: 40px;
  padding-top: 16px;
  padding-left: 20px;
  @media (max-width: 414px) {
    display: none;
  }
`;

const SuccessfullTradeText = styled.span`
  width: 300px;
  color: #100f10;
  font-family: 'Avenir Next';
  font-size: 15px;
`;

type swapTypeType = {
  swapType: string;
  swapId: string;
};

type WidthType = {
  width: number;
};

const TopWrapper = styled.div`
  width: 1055px;
  @media (max-width: 414px) {
    width: 100vw;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const PanelWrapper = styled.div`
  margin-bottom: 40px;
`;
